'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import { useGardenConfigStore } from '@shared/store/useGardenConfigStore';
import type { Garden } from '@modules/garden-config/models';
import { ComponentType, useEffect, type ReactNode } from 'react';
import { SowingConfigContainer } from './sowing_config_container';

export interface GardenConfigProps {
  isLoading: boolean;
  gardens: Garden[];
  selectedGarden: Garden | null;
  selectedGardenId: string | null;
  onGardenChange: (gardenId: string | null) => void;
  sowingContent: ReactNode;
}

interface GardenConfigContainerProps {
  View: ComponentType<GardenConfigProps>;
  SowingView: ComponentType<import('./sowing_config_container').SowingConfigProps>;
}

export function GardenConfigContainer({ View, SowingView }: GardenConfigContainerProps) {
  const gardensQuery = useQuery(api.gardens.queries.getUserGardens, {});
  const plantsQuery = useQuery(api.plants.queries.getAllPlants, {});

  const selectedGardenId = useGardenConfigStore((s) => s.selectedGardenId);
  const setSelectedGardenId = useGardenConfigStore((s) => s.setSelectedGardenId);

  const gardens = gardensQuery ?? [];

  useEffect(() => {
    if (!selectedGardenId && gardens.length > 0) {
      setSelectedGardenId(gardens[0]._id);
    }
  }, [selectedGardenId, gardens, setSelectedGardenId]);

  const isLoading = gardensQuery === undefined || plantsQuery === undefined;

  const plants = plantsQuery ?? [];

  const selectedGarden =
    selectedGardenId && !isLoading
      ? (gardens.find((g) => g._id === selectedGardenId) ?? null)
      : null;

  const sowingContent = selectedGarden ? (
    <SowingConfigContainer View={SowingView} selectedGarden={selectedGarden} plants={plants} />
  ) : null;

  return (
    <View
      isLoading={isLoading}
      gardens={gardens}
      selectedGarden={selectedGarden}
      selectedGardenId={selectedGardenId}
      onGardenChange={setSelectedGardenId}
      sowingContent={sowingContent}
    />
  );
}
